import React from 'react';
import ReactDOM from 'react-dom';
import { Provider, useSelector } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import LiveEditing from './Components/LiveEditing';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import CompareButton from "./Components/Header/CompareButton";
import Swiper from 'swiper';
import { Pagination, Navigation as swiperNavigation } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { initStickyHeader } from './Utility/Sticky.js';
import { initCompareAdded } from './Utility/CompareAdded.js';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};
function initializeComponentsByClassName(className, component) {
    Array.from(document.getElementsByClassName(className)).forEach(item => {
        renderReact(
            <Provider store={store}>
                {component}
            </Provider>,
            item
        );
    });
}

const bootstrapComponents = () => {
    if (document.getElementById('liveEditing')) {
        renderReact(
            <Provider store={store}>
                <LiveEditing />
            </Provider>,
            document.getElementById('liveEditing')
        );
    }
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Components/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Components/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }

    if (document.getElementById('quotation-checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/QuotationCheckout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('quotation-checkout')
        );
    }

    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Components/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(
            ({ lightboxImages }) => {
                store.injectReducer('lightboxImages', lightboxImages);
                const rootElement = document.getElementById('lightBoxImages');
                const images = Array.from(
                    rootElement.querySelectorAll('template')
                ).map((img) => ({
                    html: img.innerHTML,
                    src: img.dataset.src,
                }));
                renderReact(
                    <Provider store={store}>
                        <LightboxImages images={images} />
                    </Provider>,
                    document.getElementById('lightBoxImages')
                );
            }
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach(
            (slider, index) => {
                const values = [...slider.querySelectorAll('template')].map(
                    (block) => {
                        return {
                            html: block.innerHTML,
                        };
                    }
                );
                if (values.length > 0) {
                    renderReact(<Slider values={values} />, slider);
                }
            }
        );
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach(
            (button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    href,
                    cssClass,
                    label,
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <BuyButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                href,
                                cssClass,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () => import('./Components/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }

    if (document.getElementById('facetedFilterTags')) {
        const FacetedFilterTags = DynamicComponent({
            loader: () => import('./Components/FacetedFilterTags'),
        });
        renderReact(
            <Provider store={store}>
                <FacetedFilterTags />
            </Provider>,
            document.getElementById('facetedFilterTags')
        );
    }


    if (document.getElementById('specifications-table')) {
        const SpecificationsTable = DynamicComponent({
            loader: () => import('./Components/SpecificationsTable'),
        });

        const element = document.getElementById('specifications-table');
        const rows = JSON.parse(element.dataset.rows);

        renderReact(
            <Provider store={store}>
                <SpecificationsTable rows={rows} />
            </Provider>,
            document.getElementById('specifications-table')
        );
    }

    const measureUnitItems = document.querySelectorAll('[data-measurement-unit]');

    if (measureUnitItems.length > 0) {
        const Measurement = DynamicComponent({
            loader: () => import('./Components/Measurement'),
        });

        Array.from(measureUnitItems).forEach(
            (measurement) => {
                const {
                    measurementUnit
                } = measurement.dataset;

                renderReact(
                    <Provider store={store}>
                        <Measurement
                            {...{
                                measurementUnit: JSON.parse(measurementUnit)
                            }}
                        />
                    </Provider>,
                    measurement
                );
            }
        );
    }

    if (document.getElementById('resellersSearch_block')) {
        const ResellersSearch = DynamicComponent({
            loader: () => import('./Components/ResellersSearch/ResellersSearch'),
        });
        renderReact(
            <Provider store={store}>
                <ResellersSearch />
            </Provider>,
            document.getElementById('resellersSearch_block')
        );
    }

    const companyPage = document.getElementById('companyPage_map');

    if (companyPage) {
        const resellers = JSON.parse(companyPage.dataset.resellers);
        const apiKey = companyPage.dataset.apikey;

        const CompanyPage = DynamicComponent({
            loader: () => import('./Components/CompanyPage/CompanyPage'),
        });

        renderReact(
            <Provider store={store}>
                <CompanyPage resellers={resellers} googleMapsApiKey={apiKey} />
            </Provider>,
            companyPage
        );
    }

    const comparePage = document.getElementById('compare-page-block');

    if (comparePage) {
        const products = JSON.parse(comparePage.dataset.products);

        const ComparePage = DynamicComponent({
            loader: () => import('./Components/ComparePage/ComparePage'),
        });
        renderReact(
            <Provider store={store}>
                <ComparePage products={products} />
            </Provider>,
            comparePage
        );
    }


    if (document.getElementById('measurement-switch')) {
        const MeasurementSwitch = DynamicComponent({
            loader: () => import('./Components/Header/MeasurementSwitch'),
        });
        renderReact(
            <Provider store={store}>
                <MeasurementSwitch />
            </Provider>,
            document.getElementById('measurement-switch')
        );
    }


    if (document.getElementById('quick-search-modal')) {
        const QuickSearchModal = DynamicComponent({
            loader: () => import('./Components/QuickSearchModal'),
        });
        renderReact(
            <Provider store={store}>
                <QuickSearchModal />
            </Provider>,
            document.getElementById('quick-search-modal')
        );
    }

    if (document.getElementById('bottom-bar')) {
        const BottomBar = DynamicComponent({
            loader: () => import('./Components/BottomBar'),
        });
        renderReact(
            <Provider store={store}>
                <BottomBar />
            </Provider>,
            document.getElementById('bottom-bar')
        );
    }

    initializeComponentsByClassName('compare-button', <CompareButton />);
    initializeComponentsByClassName('miniCart', <MiniCart />);

    const addToCompareButtons = document.querySelectorAll('.add-to-compare-button');

    if (addToCompareButtons.length > 0) {
        
        Array.from(addToCompareButtons).forEach(
            (addToCompareButton) => {
                const {
                    articleNumber
                } = addToCompareButton.dataset;

                renderReact(
                    <Provider store={store}>
                        <AddToCompareButton
                            {...{
                                articleNumber: articleNumber
                            }}
                        />
                    </Provider>,
                    addToCompareButton
                );
            }
        );
    }

    if (document.querySelectorAll('.add-to-compare-button-container').length > 0) {
        const AddToCompareButton = DynamicComponent({
            loader: () => import('./Components/AddToCompareButton'),
        });
        Array.from(document.querySelectorAll('.add-to-compare-button-container')).forEach(
            (button, index) => {
                renderReact(<Provider store={store}><AddToCompareButton articleNumber={button.dataset.articleNumber} /></Provider>, button);
            }
        );
    }


    if (document.querySelectorAll('.product-item-add-to-compare-button-container').length > 0) {
        const ProductItemCompareButton = DynamicComponent({
            loader: () => import('./Components/ProductItem/ProductItemCompareButton'),
        });
        Array.from(document.querySelectorAll('.product-item-add-to-compare-button-container')).forEach(
            (button, index) => {
                renderReact(<Provider store={store}><ProductItemCompareButton articleNumber={button.dataset.articleNumber} text="Compare" /></Provider>, button);
            }
        );
    }


    initializeComponentsByClassName('compare-button', <CompareButton />);
    initializeComponentsByClassName('miniCart', <MiniCart />);

    if (document.getElementById('top-header-links')) {
        const TopHeaderLinks = DynamicComponent({
            loader: () => import('./Components/TopHeaderLinks'),
        });
        renderReact(
            <Provider store={store}>
                <TopHeaderLinks />
            </Provider>,
            document.getElementById('top-header-links')
        );
    }

    new Swiper(".product-listing__swiper", {
        modules: [Pagination, swiperNavigation],
        navigation: {
            nextEl: ".product-listing__swiper-button-next",
            prevEl: ".product-listing__swiper-button-prev",
        },
        pagination: {
            el: ".product-listing__swiper-pagination",
        },
        slidesPerView: 3,
        spaceBetween: 10,
        // Responsive breakpoints
        breakpoints: {
            320: {
                slidesPerView: 1.2,
                spaceBetween: 20
            },
            480: {
                slidesPerView: 1.5,
                spaceBetween: 20
            },
            768: {
                slidesPerView: 3.2,
                spaceBetween: 20
            },
            1140: {
                slidesPerView: 4.5,
                spaceBetween: 20
            }
        },
    });
};

document.addEventListener("DOMContentLoaded", () => {
    initStickyHeader();
    initCompareAdded();
});

bootstrapComponents();
